import * as queryString from 'query-string';
import * as R from 'ramda';
import * as qinit from '../../qinit';
import {
  EndpointParams,
  Method,
  RequestTransformer,
  RequestTransformerName,
} from './api';
import { getApiStore } from '../../storage/stores';
import { DateTime } from '../../lib/dateTime/types/DateTime';
import {
  getCurrentDateTime,
  getDateTimeFromISO,
  getDayOfTheYear,
  getISOfromDateTime,
  yesterday,
} from '../../lib/dateTime/index';
import { APIError } from '../../lib/errors/api/APIError';
import { APINetworkError } from '../../lib/errors/api/APINetworkError';
import { APITimeoutError } from '../../lib/errors/api/APITimeoutError';
import { FetchTimeoutError } from '../../lib/errors/api/FetchTimeoutError';
import {
  UserPasswordAccessTokenRequestBody,
  AuthorizationTokenAccessTokenRequestBody,
  DelegationAccessTokenRequestBody,
  DelegationAccessTokenResponse,
} from '../../types/api';
import { getIdentificationHeader } from '../../clientIdentification';
import { logout } from '../../onboarding/logout';

type FetchParams = Record<string, string>;

type QApiGlobals<AccessToken = string, RefreshToken = string> = {
  tokens: { accessToken?: AccessToken; refreshToken?: RefreshToken };
  clientCredentials: { client_id: string; client_secret: string };
  saveTokensCallback: (
    accessToken: AccessToken,
    refreshToken: RefreshToken
  ) => void;
};

interface RawTokens {
  access_token: string;
  refresh_token: string;
}

class QApi {
  private baseUrl: string;
  private clientCredentials: Partial<QApiGlobals['clientCredentials']> = {};
  private authTokens: Partial<RawTokens> = {};
  private saveTokensCallback: QApiGlobals['saveTokensCallback'] = () =>
    undefined;
  private timeout = 10000;
  private accountId: number | string = '';
  private accountUid = '';
  private lastUpdateLastSeen: DateTime;
  private transformers: {
    [name in RequestTransformerName]?: RequestTransformer;
  } = {};
  private isLogoutAlreadyTriggered = false;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    const apiStore = getApiStore();
    const lastUpdateLastSeenISO = apiStore.get()?.lastUpdateLastSeen;

    this.lastUpdateLastSeen = lastUpdateLastSeenISO
      ? getDateTimeFromISO(lastUpdateLastSeenISO)
      : yesterday();
  }

  private get defaultParamValuesFromCurrentAccount(): DefaultParamValues {
    return {
      accountId: this.accountId,
      accountUid: this.accountUid,
      profileId: '',
    };
  }

  private getActionHeader() {
    let action: string | undefined;
    const lastDay = getDayOfTheYear(this.lastUpdateLastSeen);
    const now = getCurrentDateTime();
    const today = getDayOfTheYear(now);

    if (lastDay !== today) {
      action = 'update-last-seen';
      this.lastUpdateLastSeen = now;
      getApiStore().set({
        lastUpdateLastSeen: getISOfromDateTime(now),
      });
    }
    return action
      ? {
          'Parent-Device-Action': action,
        }
      : {};
  }

  private getClientHeaders() {
    return {
      ...this.getActionHeader(),
      ...getIdentificationHeader(),
    };
  }

  private getAuthorizationHeaders(): HeadersInit {
    return this.authTokens && this.authTokens.access_token
      ? { Authorization: `Bearer ${this.authTokens.access_token}` }
      : {};
  }

  private getRequestHeaders(headers: Partial<HeadersInit> = {}) {
    return Object.assign(
      this.getAuthorizationHeaders(),
      this.getClientHeaders(),
      headers
    );
  }

  private updateTokens<Tokens extends RawTokens = RawTokens>(
    options: RequestInit,
    grant: Tokens
  ) {
    this.authTokens = {
      access_token: grant.access_token,
      refresh_token: grant.refresh_token,
    };
    this.saveTokensCallback(grant.access_token, grant.refresh_token);
    return Object.assign(options, {
      headers: Object.assign(
        options.headers || {},
        this.getAuthorizationHeaders()
      ),
    }) as RequestInit;
  }

  private refreshAccessToken() {
    const { refresh_token } = this.authTokens;
    const { client_id, client_secret } = this.clientCredentials;

    /**
     * check if all the body fields are set and throw an error if not
     * @see {@link https://qustodio.atlassian.net/browse/CHR-166 | Jira ticket}
     */
    const body = {
      grant_type: 'refresh_token',
      refresh_token: refresh_token as string,
      client_id: client_id as string,
      client_secret: client_secret as string,
    };
    return this.accessToken.post(body);
  }

  private async refreshTokenIfNeeded(
    url: string,
    options: RequestInit,
    response: Response
  ) {
    if (response.status === APIError.STATUS_UNAUTHORIZED) {
      const token = await this.refreshAccessToken();
      this.updateTokens(options, token);
      return fetch(url, options);
    }
    return response;
  }

  /**
   * Logout the extension if the response code is 410.
   */
  private async logoutIfNeeded(response: Response) {
    // Because in the logout we do a call as well to remove the device,
    // we need to avoid triggering this logic again, or we could end up in a loop.
    if (
      response.status === APIError.STATUS_GONE &&
      !this.isLogoutAlreadyTriggered
    ) {
      this.isLogoutAlreadyTriggered = true;
      try {
        await logout();
      } finally {
        this.isLogoutAlreadyTriggered = false;
      }
    }
    return response;
  }

  public toJson<T>(response: Response) {
    if (response.headers && response.headers.get) {
      const contentType = response.headers.get('content-type');
      if (contentType && R.contains('application/json', contentType)) {
        return response.json() as Promise<T>;
      }
    }
  }

  private toError<RequestBody>(
    endpoint: string,
    method: Method,
    body: RequestBody
  ) {
    return (response: Response) => {
      if (response.ok) {
        // HTTP status is 200..299
        return response;
      }
      throw new APIError(response, endpoint, method, body);
    };
  }

  private trapFetchError<RequestBody>(
    endpoint: string,
    method: Method,
    body: RequestBody
  ) {
    return (error: Error) =>
      Promise.reject(
        error instanceof FetchTimeoutError
          ? new APITimeoutError(
              {
                status: APIError.STATUS_TIMEOUT,
                statusText: error.message,
              },
              endpoint,
              method,
              body
            )
          : new APINetworkError(
              {
                status: APIError.STATUS_NETWORK_ERROR,
                statusText: error.message,
              },
              endpoint,
              method,
              body
            )
      );
  }

  private timeoutFetch(
    input: RequestInfo,
    init?: RequestInit
  ): Promise<Response> {
    return Promise.race([
      fetch(input, init),
      new Promise<never>((_resolve, reject) =>
        setTimeout(
          () =>
            reject(
              new FetchTimeoutError(`fetch timeout of ${this.timeout} exceeded`)
            ),
          this.timeout
        )
      ),
    ]);
  }

  /**
   * Replace the path params in the path with the values in the pathParams
   * object. Also add the instance accountId and accountUid to the pathParams if
   * they are not already present.
   *
   * @param path - The path to replace the params in. The params should be in the
   * format `{paramName}`. For example: `/api/v1/accounts/{accountId}/devices`
   */
  private getParametrizedPath(
    path: string,
    pathParams: FetchParams = {}
  ): string {
    return R.applyTo(
      pathParams,
      R.pipe(
        R.mergeRight(this.defaultParamValuesFromCurrentAccount),
        R.toPairs,
        R.reduce(
          (newPath, [key, value]) =>
            R.replace(`{${key}}`, String(value), newPath),
          `/${path}`
        )
      )
    );
  }

  private fetchApi<T>(
    path: string,
    pathParams: FetchParams | undefined,
    queryParams: FetchParams | undefined,
    options: RequestInit,
    endpoint: string
  ) {
    if (R.isEmpty(path)) {
      return Promise.reject(new Error('"path" must not be empty'));
    }

    if (qinit.application.type !== 'dashboard') {
      if (
        !this.authTokens ||
        (!this.authTokens.access_token && !this.authTokens.refresh_token)
      ) {
        return Promise.reject(
          new APIError(
            {
              status: APIError.STATUS_MISSING_TOKENS_ERROR,
              statusText: `No access/refresh tokens for request /${endpoint}`,
            },
            endpoint,
            options.method as string,
            options.body
          )
        );
      }
    }

    const parametrizedPath = this.getParametrizedPath(path, pathParams);

    const url = R.join('', [
      this.baseUrl,
      parametrizedPath,
      Object.keys(queryParams || {}).length > 0
        ? `?${queryString.stringify(queryParams || {})}`
        : '',
    ]);
    const moreOptions =
      qinit.application.type === 'dashboard'
        ? { timeout: this.timeout, credentials: 'same-origin' }
        : { timeout: this.timeout, cache: 'no-store' };

    return this.timeoutFetch(url, Object.assign(moreOptions, options))
      .catch(
        this.trapFetchError(endpoint, options.method as Method, options.body)
      )
      .then(this.refreshTokenIfNeeded.bind(this, url, options))
      .then(this.logoutIfNeeded.bind(this))
      .then(this.toError(endpoint, options.method as Method, options.body))
      .then(this.toJson<T>);
  }

  public setGlobals(globals: QApiGlobals): void {
    if (globals.tokens) {
      this.authTokens = {
        access_token: globals.tokens.accessToken,
        refresh_token: globals.tokens.refreshToken,
      };
    }
    this.setClientCredentials(globals.clientCredentials);
    this.saveTokensCallback = globals.saveTokensCallback;
  }

  public setAccountId(id: number | string): void {
    this.accountId = id;
  }

  public setAccountUid(uid: string): void {
    this.accountUid = uid;
  }

  public setClientCredentials(
    clientCredentials: QApiGlobals['clientCredentials']
  ) {
    this.clientCredentials = clientCredentials;
  }

  public addRequestTransformer(transformer: RequestTransformer) {
    this.transformers[transformer.name] = transformer;
  }

  public get<T>(
    endpoint: string,
    path: string,
    params?: FetchParams,
    query?: FetchParams
  ) {
    return this.fetchApi<T>(
      path,
      params,
      query,
      {
        method: 'GET',
        headers: this.getRequestHeaders(),
      },
      endpoint
    );
  }

  // @TODO change post, put and delete to private once referenced by TS
  public post<T>(
    endpoint: string,
    path: string,
    body: Record<string | number, unknown> = {},
    query?: FetchParams,
    params?: FetchParams
  ) {
    return this.fetchApi<T>(
      path,
      params,
      query,
      {
        method: 'POST',
        headers: this.getRequestHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(body),
      },
      endpoint
    );
  }

  public put<T>(
    endpoint: string,
    path: string,
    params?: FetchParams,
    body = {},
    query?: FetchParams
  ) {
    return this.fetchApi<T>(
      path,
      params,
      query,
      {
        method: 'PUT',
        headers: this.getRequestHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(body),
      },
      endpoint
    );
  }

  public delete<T>(
    endpoint: string,
    path: string,
    params?: FetchParams,
    query?: FetchParams
  ) {
    return this.fetchApi<T>(
      path,
      params,
      query,
      {
        method: 'DELETE',
        headers: this.getRequestHeaders(),
      },
      endpoint
    );
  }

  private endpoint<
    SupportedMethods extends Method,
    Params extends object = never
  >(
    name: EndpointParams<Params>['name'],
    methods: SupportedMethods[],
    path: EndpointParams<Params>['path']
  ) {
    const processParams = (params: Params) =>
      (
        Object.values(
          this.transformers
        ) as unknown as RequestTransformer<Params>[]
      ).reduce((params, transformer) => {
        if (transformer.transformApplies({ name, methods, path })) {
          return transformer.transform(params);
        }
        return params;
      }, params);

    return methods.reduce(
      (endpoint, method) =>
        Object.assign(endpoint, {
          [method]:
            method !== 'post'
              ? (params: Params = {} as Params, ...rest: FetchParams[]) =>
                  this[method].call(
                    this,
                    name,
                    path(
                      processParams({
                        ...this.defaultParamValuesFromCurrentAccount,
                        ...params,
                      })
                    ),
                    {},
                    ...rest
                  )
              : // qapi.post has switched param order
                (
                  body: Record<string | number, unknown>,
                  query?: FetchParams,
                  params: Params = {} as Params
                ) =>
                  this.post.call(
                    this,
                    name,
                    path(
                      processParams({
                        ...this.defaultParamValuesFromCurrentAccount,
                        ...params,
                      })
                    ),
                    body,
                    query,
                    {}
                  ),
        }),
      {} as Endpoint<SupportedMethods, Params>
    );
  }

  public profiles = this.endpoint(
    'profiles',
    ['get', 'post', 'put', 'delete'],
    (params: DefaultParamValues) =>
      `v1/accounts/${params.accountId}/profiles/${params.profileId}`
  );

  public me = this.endpoint('me', ['get'], () => `v1/accounts/me`);

  public devices = this.endpoint(
    'devices',
    ['get', 'post'],
    (params: DefaultParamValues) => `v2/accounts/${params.accountUid}/devices`
  );

  public device = this.endpoint(
    'device',
    ['get', 'put', 'delete'],
    (params: { deviceUid: PathId } & DefaultParamValues) =>
      `v2/accounts/${params.accountUid}/devices/${params.deviceUid}`
  );

  public deviceDetails = this.endpoint(
    'device',
    ['post'],
    (params: { deviceUID: UId } & DefaultParamValues) =>
      `v1/accounts/${params.accountId}/devices/${params.deviceUID}/details`
  );

  public deviceOptions = this.endpoint(
    'device',
    ['post'],
    (params: { deviceUid: UId; userUid: UId } & DefaultParamValues) =>
      `v1/accounts/${params.accountId}/devices/${params.deviceUid}/users/${params.userUid}/options`
  );

  public deviceUserEvents = this.endpoint(
    'deviceUserEvents',
    ['post'],
    (params: { deviceUId: UId; userUId: UId } & DefaultParamValues) =>
      `v2/accounts/${params.accountUid}/devices/${params.deviceUId}/users/${params.userUId}/events`
  );

  public deviceVersion = this.endpoint(
    'deviceVersion',
    ['post'],
    (params: { deviceUID: UId } & DefaultParamValues) =>
      `v1/accounts/${params.accountId}/devices/${params.deviceUID}/version`
  );

  /**
   * The oauth2/access_token endpoint is an exception. The content type is application/x-www-form-urlencoded, and
   * errors are handled in a much simpler way: everything except status: 200 is treated as error.
   */
  public accessToken = {
    post: <
      RequestBody extends
        | UserPasswordAccessTokenRequestBody
        | AuthorizationTokenAccessTokenRequestBody,
      ResponseBody extends RawTokens = RawTokens
    >(
      body: RequestBody
    ) => {
      return this.timeoutFetch(`${this.baseUrl}/v1/oauth2/access_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...this.getClientHeaders(),
        } as HeadersInit,
        body: queryString.stringify(body),
      })
        .catch(this.trapFetchError('access_token', 'POST' as Method, body))
        .then(response => {
          if (response.status !== 200) {
            throw new APIError(response, 'access_token', 'POST', body);
          }
          return response.json() as Promise<ResponseBody>;
        });
    },
  };

  /**
   * Authenticate the device with delegation.
   *
   * @remarks
   * Use it only to onboard the device. To refresh the token, use the regular endpoint: v1/oauth2/access_token.
   */
  public delegationAccessToken = {
    post: <
      RequestBody extends DelegationAccessTokenRequestBody,
      ResponseBody extends DelegationAccessTokenResponse
    >(
      body: RequestBody
    ) => {
      return this.timeoutFetch(
        `${this.baseUrl}/v2/partners/lw-school/authentication/access_token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.getClientHeaders(),
          } as HeadersInit,
          body: JSON.stringify(body),
        }
      )
        .catch(this.trapFetchError('access_token', 'POST' as Method, body))
        .then(response => {
          if (response.status !== 200) {
            throw new APIError(response, 'access_token', 'POST', body);
          }
          return response.json() as Promise<ResponseBody>;
        });
    },
  };

  public deviceRules = this.endpoint(
    'deviceRules',
    ['get'],
    (params: { deviceUId: UId } & DefaultParamValues) =>
      `v2/accounts/${params.accountUid}/devices/${params.deviceUId}/rules`
  );

  public safeNetworksSettings = this.endpoint(
    'safeNetworksSettings',
    ['get'],
    (params: { deviceUId: UId } & DefaultParamValues) =>
      `v2/accounts/${params.accountUid}/devices/${params.deviceUId}/safe-networks/settings`
  );

  public safeNetworksEvent = this.endpoint(
    'safeNetworksEvent',
    ['post'],
    (params: { deviceUid: UId; userUid: UId } & DefaultParamValues) =>
      `v2/accounts/${params.accountUid}/devices/${params.deviceUid}/users/${params.userUid}/safe_networks_event`
  );

  public sitesClassification = this.endpoint(
    'sitesClassification',
    ['get'],
    (params: { hostname: string }) =>
      `v1/sites/classification?hostname=${params.hostname}`
  );

  public pushNotificationToken = this.endpoint(
    'pushNotificationToken',
    ['post'],
    (params: { deviceUid: UId; userUid: UId } & DefaultParamValues) =>
      `v1/accounts/${params.accountId}/devices/${params.deviceUid}/users/${params.userUid}/pushnotifications`
  );

  public uploadsPresignedUrls = this.endpoint(
    'uploadsPresignedUrls',
    ['post'],
    (params: DefaultParamValues) =>
      `v2/download/accounts/${params.accountUid}/uploads-presigned-urls`
  );
}

type PathId = string | number;
type UId = string;

type DefaultParamValues = {
  accountId?: PathId;
  accountUid?: UId;
  profileId?: PathId;
};

type Endpoint<T extends Method, U> = { [P in T]: EndpointMethod<U>[P] };

/**
 * This type is used to define the methods that can be used in an endpoint.
 *
 * NOTE: The template types are very generic. This is why are using `any` in the
 * template default type
 */
type EndpointMethod<Params> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: <T = any, Q = any>(params?: Params, query?: Q) => Promise<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post: <T = any, B = any, Q = any>(
    body: B,
    query?: Q,
    params?: Params
  ) => Promise<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  put: <T = any, B = any, Q = any>(
    params: Params | undefined,
    body: B,
    query?: Q
  ) => Promise<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  delete: <T = any, Q = any>(params?: Params, query?: Q) => Promise<T>;
};

export default QApi;
